import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip, useNotify } from '@routes-plan/ui';
import { Client } from '../../../models/client';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import PermissionButton from '../../../components/common/PermissionButton';
import ClientChangeHistoryModal from '../../../components/common/ClientChangeHistoryModal';
import ClientQuoteModal from '../../../components/common/ClientQuoteModal';
import RatingStars from '../../../components/rating/RatingStars';
import { useSelection } from '../../../components/context/SelectionContext';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { formatCurrency } from '../../../utils/format/currency';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, ContentCard, FilterRow, RowAction, RowActions, useAlert } from '../shared/AdminUi';
import ClientEraseModal from './ClientEraseModal';
import ClientTrashModal from './ClientTrashModal';

type RowId = string | number;

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface ClientRow {
  id: number;
  idNumber: string;
  fullName: string;
  phone: string;
  mobilePhone: string;
  mainAddress: string;
  job: string;
  creditLimit: number;
  backupAlertActive: boolean;
  hasActiveCredits: boolean;
  sharedFrom: string;
  ratingScore: number | null;
  sourceClient: Client;
}

const ClientList: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = useAlert();
  setPageTitle('clients.pageTitle');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClientForHistory, setSelectedClientForHistory] = useState<Client | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedClientForQuote, setSelectedClientForQuote] = useState<Client | null>(null);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const { office_id, setOffice_id, collect_id, setCollect_id } = useSelection();

  // Selección para eliminar varios a la vez.
  const [selectedIds, setSelectedIds] = useState<RowId[]>([]);
  const { canCreate, canRead, canUpdate, canDelete, canUpdateQuote, canManageBackupAlert } = useResourcePermissions('/admin/clients');

  const [refresh, setRefresh] = useState(false);
  const sortByName = <T extends { name?: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'es'));

  const handleOpenModal = (client: Client) => {
    setSelectedClientForHistory(client);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClientForHistory(null);
  };
  const handleOpenQuoteModal = (client: Client) => {
    setSelectedClientForQuote(client);
    setQuoteModalOpen(true);
  };
  const handleCloseQuoteModal = () => {
    setQuoteModalOpen(false);
    setSelectedClientForQuote(null);
  };
  const handleRefresh = () => setRefresh(prev => !prev);

  // Eliminar: abre la confirmación (se borran sus datos; préstamos y abonos quedan; 30 días de papelera).
  const [eraseIds, setEraseIds] = useState<number[]>([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const handleDeleteSelected = (idToDelete?: RowId) => {
    if (!canDelete) return;
    const idsToDelete: RowId[] = idToDelete !== undefined ? [idToDelete] : selectedIds;
    if (idsToDelete.length === 0) {
      showAlert(t('clients.selectToDelete'), "warning");
      return;
    }
    const numericIds = idsToDelete.map(id => (typeof id === 'string' ? Number(id) : id))
      .filter(n => Number.isFinite(n)) as number[];
    if (numericIds.length === 0) {
      showAlert(t('clients.invalidIds'), "error");
      return;
    }
    setEraseIds(numericIds);
  };
  const handleErased = (message: string, severity: 'success' | 'warning' | 'error', erasedIds: number[]) => {
    setEraseIds([]);
    showAlert(message, severity);
    if (erasedIds.length) {
      setSelectedIds(prev => prev.filter(id => !erasedIds.includes(typeof id === 'string' ? Number(id) : id)));
      handleRefresh();
    }
  };

  // Cargar sedes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const officesResponse = axios.get('offices');
        const officeRoutesResponse = office_id ? axios.get(`offices/${office_id}/routes`) : null;

        const [officesData, officeRoutesData] = await Promise.all([
          officesResponse,
          officeRoutesResponse,
        ]);

        if (officesData) {
          const officesList = Array.isArray(officesData?.data?.data) ? officesData.data.data : [];
          setOffices(sortByName(officesList));
        }
        if (officeRoutesData) {
          const routesList = Array.isArray(officeRoutesData?.data) ? officeRoutesData.data : [];
          setOfficeRoutes(sortByName(routesList));
        }
        setClients([]);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
  }, [office_id, refresh]);

  // Cargar clientes de la ruta
  useEffect(() => {
    const fetchRouteClients = async () => {
      if (!collect_id) {
        setClients([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await axios.get(`routes/${collect_id}/clients`);
        setClients(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        showAlert(t('common.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchRouteClients();
  }, [collect_id, refresh]);


  const rows: ClientRow[] = clients.map((client: Client) => ({
    id: Number(client.id),
    idNumber: String(client.idNumber ?? ''),
    fullName: client.fullName ?? '',
    phone: String(client.phone ?? ''),
    mobilePhone: String(client.mobilePhone ?? ''),
    mainAddress: client.mainAddress ?? '',
    job: client.job ?? '',
    creditLimit: Number(client.creditLimit ?? 0),
    backupAlertActive: Boolean(client.backupAlertActive),
    hasActiveCredits: Boolean((client as any).hasActiveCredits ?? false),
    // Sede con clientes compartidos: cliente creado en otra ruta.
    sharedFrom: (client as any)?.sharedFrom?.name ?? '',
    ratingScore: client.ratingScore ?? null,
    sourceClient: client,
  }));

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
  const someSelected = rows.some((r) => selectedIds.includes(r.id));
  const toggleRow = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addButton = (
    <PermissionButton
      allowed={canCreate}
      deniedTooltip={t('common.noPermissionCreate')}
      variant="contained"
      startIcon={<AddIcon />}
      component={NavLink}
      to="/admin/clients/new"
      data-tour="client-new"
    >
      {t('clients.newTitle')}
    </PermissionButton>
  );

  return (
    <AdminPage>
      <PageHeader
        title={t('clients.title')}
        description={t('clients.subtitle')}
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canDelete && (
              <Button variant="outlined" color="inherit" onClick={() => setTrashOpen(true)} startIcon={<RestoreFromTrashOutlinedIcon />}>
                {t('clients.trash.button')}
              </Button>
            )}
            {addButton}
          </Box>
        }
      />
      <ContentCard>
        <FilterRow sx={{ mb: 2 }}>
          <TextField
            id="clients-office"
            select
            size="small"
            label={t('clients.office')}
            value={offices.length ? office_id ?? '' : ''}
            onChange={(event) => setOffice_id(event.target.value)}
            fullWidth
          >
            {offices.map((office: Office) => (
              <MenuItem key={office.id} value={office.id}>
                {office.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="clients-route"
            select
            size="small"
            label={t('clients.route')}
            value={officeRoutes.length ? collect_id ?? '' : ''}
            onChange={(event) => setCollect_id(event.target.value)}
            disabled={!office_id || officeRoutes.length === 0}
            fullWidth
          >
            {officeRoutes.map((route: Route) => (
              <MenuItem key={route.id} value={route.id}>
                {route.name}
              </MenuItem>
            ))}
          </TextField>
        </FilterRow>
        <DataTable<ClientRow>
          rows={rows}
          loading={loading}
          getRowId={(r) => r.id}
          ariaLabel={t('clients.title')}
          searchable
          searchPlaceholder={t('clients.searchPlaceholder')}
          exportFileName={t('clients.exportFileName')}
          initialSort={{ columnId: 'fullName', direction: 'asc' }}
          toolbarActions={
            canDelete && someSelected ? (
              <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => handleDeleteSelected()}>
                {t('clients.deleteSelected', { count: selectedIds.length })}
              </Button>
            ) : undefined
          }
          columns={[
            {
              id: 'fullName',
              header: canDelete ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setSelectedIds(e.target.checked ? rows.map((r) => r.id) : [])}
                    inputProps={{ 'aria-label': t('clients.selectAll') }}
                    sx={{ p: 0.5, ml: -0.5 }}
                  />
                  {t('clients.client')}
                </Box>
              ) : (
                t('clients.client')
              ),
              csvHeader: t('clients.client'),
              value: (r) => r.fullName,
              sortable: true,
              render: (r) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  {canDelete && (
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(r.id)}
                      onChange={() => toggleRow(r.id)}
                      inputProps={{ 'aria-label': `${t('common.select')} ${r.fullName}` }}
                      sx={{ p: 0.5, ml: -0.5 }}
                    />
                  )}
                  {!r.hasActiveCredits && (
                    <Tooltip title={t('clients.noActiveLoan')}>
                      <WarningAmberOutlinedIcon color="warning" fontSize="small" aria-label={t('clients.noActiveLoan')} sx={{ flexShrink: 0 }} />
                    </Tooltip>
                  )}
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.fullName}
                  </Box>
                  {r.sharedFrom && <StatusChip label={t('clients.shared.from', { route: r.sharedFrom })} tone="info" dot={false} />}
                </Box>
              ),
            },
            { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true },
            {
              id: 'rating',
              header: t('rating.score'),
              value: (r) => r.ratingScore,
              render: (r) => <RatingStars score={r.ratingScore} compact />,
              sortable: true,
              hideBelow: 'sm',
            },
            { id: 'mobilePhone', header: t('common.cellphone'), value: (r) => r.mobilePhone, hideBelow: 'sm' },
            { id: 'phone', header: t('common.phone'), value: (r) => r.phone, hideBelow: 'md' },
            { id: 'mainAddress', header: t('common.address'), value: (r) => r.mainAddress, wrap: true, width: 220, hideBelow: 'md' },
            { id: 'job', header: t('clients.job'), value: (r) => r.job, hideBelow: 'md' },
            {
              id: 'creditLimit',
              header: t('clients.creditLimit'),
              value: (r) => r.creditLimit,
              render: (r) => formatCurrency(r.creditLimit),
              align: 'right',
              sortable: true,
            },
          ]}
          rowActions={(row) => (
            <RowActions>
              <span data-tour="client-history">
                <RowAction
                  label={`${row.backupAlertActive ? t('clientChanges.pending') : t('clients.viewChanges')}: ${row.fullName}`}
                  icon={row.backupAlertActive ? <NotificationImportantOutlinedIcon fontSize="small" /> : <HistoryOutlinedIcon fontSize="small" />}
                  color={row.backupAlertActive ? 'warning' : 'default'}
                  allowed={canRead}
                  deniedTooltip={t('clients.noPermissionViewChanges')}
                  onClick={() => handleOpenModal(row.sourceClient)}
                />
              </span>
              <RowAction
                label={`${t('clients.editCreditLimit')}: ${row.fullName}`}
                icon={<CreditScoreOutlinedIcon fontSize="small" />}
                allowed={canUpdateQuote}
                deniedTooltip={t('clients.noPermissionEditCreditLimit')}
                onClick={() => handleOpenQuoteModal(row.sourceClient)}
              />
              <RowAction
                label={`${t('common.edit')} ${row.fullName}`}
                icon={<EditOutlinedIcon fontSize="small" />}
                allowed={canUpdate}
                deniedTooltip={t('common.noPermissionEdit')}
                to={`/admin/clients/${row.id}/edit`}
              />
              <RowAction
                label={`${t('common.delete')} ${row.fullName}`}
                icon={<DeleteOutlineIcon fontSize="small" />}
                color="error"
                allowed={canDelete}
                deniedTooltip={t('common.noPermissionDelete')}
                onClick={() => handleDeleteSelected(row.id)}
              />
            </RowActions>
          )}
          emptyState={
            <EmptyState
              icon={<GroupsOutlinedIcon />}
              title={collect_id ? t('clients.emptyTitle') : t('clients.chooseRoute')}
              description={collect_id ? t('clients.emptyDescription') : t('clients.chooseRouteHint')}
              action={collect_id && canCreate ? addButton : undefined}
            />
          }
        />
      </ContentCard>
      <ClientChangeHistoryModal
        open={isModalOpen}
        onClose={handleCloseModal}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setClients((current) => current.map((client) => (client.id === clientId ? { ...client, backupAlertActive: active } : client)));
          setSelectedClientForHistory((current) => (current && current.id === clientId ? { ...current, backupAlertActive: active } : current));
        }}
        clientData={
          selectedClientForHistory
            ? {
                id: selectedClientForHistory.id,
                fullName: selectedClientForHistory.fullName,
                backupAlertActive: Boolean(selectedClientForHistory.backupAlertActive),
                backups: (selectedClientForHistory as any).backups || [],
              }
            : null
        }
      />
      <ClientQuoteModal
        open={quoteModalOpen}
        client={selectedClientForQuote}
        onClose={handleCloseQuoteModal}
        onSaved={(nextQuote) => {
          setClients((current) =>
            current.map((client) => (client.id === selectedClientForQuote?.id ? { ...client, creditLimit: nextQuote } : client)),
          );
        }}
        showAlert={showAlert}
      />
      <ClientEraseModal ids={eraseIds} open={eraseIds.length > 0} onClose={() => setEraseIds([])} onDone={handleErased} />
      <ClientTrashModal
        open={trashOpen}
        onClose={() => setTrashOpen(false)}
        onRestored={(message, severity) => {
          showAlert(message, severity);
          if (severity === 'success') handleRefresh();
        }}
      />
    </AdminPage>
  );
};

export default ClientList;
